import { DestroyRef, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { NOT_FOUND_ROUTE } from '../../constants/auth.routing.constants';
import { WorkspaceHelper } from '../workspace/workspace.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Workspace } from '../../models/workspace.model';
import { ApiResponse } from '../../models/api-response.model';
import { ToasterService } from '../../../../ui-components/services/toast/toast.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UrlListenerHelper {
  readonly authService = inject(AuthService);
  readonly workspaceHelper = inject(WorkspaceHelper);
  readonly destroyRef = inject(DestroyRef);
  readonly toast = inject(ToasterService);
  readonly router = inject(Router);

  /**
   * On a domain change while authenticated with a selected workspace:
   * - same domain as the selection -> nothing to do
   * - different domain -> ask the tenants endpoint. If it's one of the user's
   *   workspaces, switch to it; otherwise send them to the central not-found.
   */
  listenToUrlChanges(): void {
    const selected = this.authService.selectedWorkspace();
    if (!this.authService.isAuthenticated() || !selected) {
      return;
    }

    // Current domain already matches the selected workspace -> nothing to do.
    if (
      selected.urls.web.includes(location.host) ||
      location.host.includes('localhost')
    ) {
      return;
    }

    this.validateDomainAgainstTenants();
  }

  private validateDomainAgainstTenants(): void {
    this.authService
      .centralWorkspace()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ApiResponse<Workspace>) => {
          const matched = (response.data || []).find(workspace =>
            workspace.urls.web.includes(location.host)
          );

          // The domain is one of the user's workspaces -> switch to it.
          if (matched) {
            this.workspaceHelper.selectWorkspace(matched);
            return;
          }

          // Not one of the user's workspaces -> central not-found.
          this.toast.add(
            'this workspace is not available',
            '',
            '',
            'error',
            true,
            true
          );
          if (location.hostname.includes('localhost')) {
            this.router.navigate(['/' + NOT_FOUND_ROUTE]);
          } else {
            location.href = `https://${environment.domain}/${NOT_FOUND_ROUTE}`;
          }
        },
      });
  }
}
